import React from 'react';
import { Input } from '@/components/Input';
import IconButton from '@/ctComponents/IconButton';
import Modal from '@/ctComponents/Modal';
import { useEffect, useState, useRef } from 'react';
import { useNotify } from 'react-admin';
import { noScroll, removeNoScroll } from '@/actions/Helper';
import { ReactSVG } from 'react-svg';
import { Icons } from '@/assets';
import { importChassis, saveChassis, SaveApiResMessage } from '@/actions';

export const EditChassis = ({
  show,
  setShow,
  chassisList = [],
  setChassisList,
}) => {
  // const notify = useNotify();

  const [newChassis, setNewChassis] = useState('');
  const [chassisError, setChassisError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [internalChassisList, setInternalChassisList] = useState([]);
  const chassisListRef = useRef(null);

  useEffect(() => {
    show ? noScroll() : removeNoScroll();
  }, [show]);

  // Initialize internal state when modal opens
  useEffect(() => {
    if (show) {
      setInternalChassisList([...chassisList]);
      setChassisError(''); // Clear any error when modal opens
    }
  }, [show, chassisList]);

  const handleAddChassis = () => {
    if (newChassis.trim()) {
      // Check for duplicates (case-insensitive)
      const isDuplicate = internalChassisList.some(
        (chassis) =>
          chassis.name.toLowerCase() === newChassis.trim().toLowerCase()
      );

      if (isDuplicate) {
        setChassisError('This chassis name already exists in the list');
        return;
      }

      const newChassisItem = {
        id: Date.now(),
        name: newChassis.trim(),
      };
      setInternalChassisList((prev) => [...prev, newChassisItem]);
      setNewChassis('');
      setChassisError(''); // Clear any existing error
      // notify('Chassis added successfully', 'success');
      SaveApiResMessage({
        title: null,
        message: 'Chassis added successfully',
        mode: 'success',
      });

      // Scroll to bottom after state update
      setTimeout(() => {
        if (chassisListRef.current) {
          chassisListRef.current.scrollTop =
            chassisListRef.current.scrollHeight;
        }
      }, 100);
    }
  };

  const handleChassisInputChange = (e) => {
    setNewChassis(e.target.value);
    // Clear error when user starts typing
    if (chassisError) {
      setChassisError('');
    }
  };

  const handleRemoveChassis = (id) => {
    setInternalChassisList((prev) =>
      prev.filter((chassis) => chassis.id !== id)
    );
    // notify('Chassis removed successfully', 'success');
    SaveApiResMessage({
      title: null,
      message: 'Chassis removed successfully',
      mode: 'success',
    });
  };

  const handleImportXLSX = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,.csv';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setIsImporting(true);
        let formData = new FormData();
        formData.append('document', file);
        importChassis(formData)
          .then((res) => {
            setIsImporting(false);

            setInternalChassisList(res.data['own-chases']);
            if (res.message) {
              // notify(res.message, 'success');
              SaveApiResMessage({
                title: res?.data?.messageHeader,
                message: res.message,
                mode: 'success',
              });
            } else {
              // notify('Chassis Successfully Updated!', 'success');
              SaveApiResMessage({
                title: res?.data?.messageHeader,
                message: 'Chassis Successfully Updated!',
                mode: 'success',
              });
            }
          })
          .catch((err) => {
            setIsImporting(false);
            // notify(err.message || err || 'Failed to import chassis', 'warning');
            SaveApiResMessage({
              title: 'Error',
              message: err.message || err || 'Failed to import chassis',
              mode: 'warning',
            });
          });
      }
    };
    input.click();
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const formData = internalChassisList.map((item) => {
        return item.name;
      });
      console.log({ formData });
      const res = await saveChassis({ chases: formData });

      // Update parent component's state only after successful save
      setChassisList(res.chases);
      // notify('Chassis Successfully Updated!', 'success');
      SaveApiResMessage({
        title: res?.data?.messageHeader,
        message: 'Chassis Successfully Updated!',
        mode: 'success',
      });
      setShow(false);
    } catch (error) {
      // notify(error.message || 'Failed to save chassis', 'warning');
      SaveApiResMessage({
        title: 'Error',
        message: error.message || 'Failed to save chassis',
        mode: 'warning',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setNewChassis('');
    setChassisError(''); // Clear any error when canceling
    setShow(false);
  };

  return (
    <Modal
      minWidth="unset"
      show={show}
      notClose={true}
      rightContent={
        <div className="flex justify-start">
          <IconButton
            onClick={handleImportXLSX}
            text={isImporting ? 'Importing...' : 'Import XLSX'}
            color="#131517"
            size="normal"
            disabled={isImporting}
            style={{
              border: 'none',
              opacity: isImporting ? 0.7 : 1,
            }}
          />
        </div>
      }
      children={
        <div className="min-w-300 sm:min-w-xl py-4 w-full overflow-y-auto space-y-4 lg:max-w-xl">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Input
                className="text-left"
                label="Add New Chassis"
                name="chassis"
                id="chassis"
                placeholder="Enter your chassis"
                value={newChassis}
                onChange={handleChassisInputChange}
                onKeyPress={(e) => e.key === 'Enter' && handleAddChassis()}
                error={chassisError}
              />
            </div>
            <IconButton
              onClick={handleAddChassis}
              text="Add to List"
              icon="Add"
              color="#131617"
              size="normal"
              style={{
                height: '48px',
                marginBottom: chassisError ? '20px' : '0px',
              }}
            />
          </div>

          {internalChassisList.length > 0 && (
            <div
              className="mt-8 border border-solid rounded-sm"
              id="chassis-list">
              <div ref={chassisListRef} className="max-h-432 overflow-y-auto">
                {internalChassisList.map((chassis, index) => (
                  <div
                    key={chassis.id}
                    className="flex items-center justify-between p-3 border-b border-solid border-l-0 border-r-0 border-t-0 border-gray-200 ">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-primary-text min-w-[30px]">
                        {index + 1}:
                      </span>
                      <span className="text-secondary-text text-sm">
                        {chassis.name}
                      </span>
                    </div>

                    <button
                      className="bg-transparent border-none"
                      onClick={() => handleRemoveChassis(chassis.id)}>
                      <ReactSVG
                        src={Icons.deleteIcon}
                        className=""
                        beforeInjection={(svg) => {
                          svg.setAttribute('width', '20');
                          svg.setAttribute('height', '20');
                        }}
                      />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 mt-6 pt-4 ">
            <IconButton
              onClick={handleCancel}
              text="Cancel"
              color="#131617"
              size="normal"
              style={{
                minWidth: 120,
                height: 40,
              }}
            />
            <IconButton
              onClick={handleSave}
              text={isSubmitting ? 'Saving...' : 'Save Changes'}
              color="#FFAD00"
              size="normal"
              disabled={isSubmitting}
              style={{
                backgroundColor: '#FFAD00',
                color: '#131617',
                opacity: isSubmitting ? 0.7 : 1,
                minWidth: 120,
                height: 40,
              }}
            />
          </div>
        </div>
      }
      icon="pen"
      title="Edit Own Chassis List"
      setShow={setShow}
    />
  );
};
